import React, { useState, useEffect } from 'react';
import { apiService } from '../../utils/api';
import { FaUser, FaEdit, FaSave, FaTimes, FaGraduationCap, FaCalendarAlt, FaCheckCircle } from 'react-icons/fa';
import './StudentProfile.css';

function StudentProfile({ user }) {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    username: user.username,
    email: user.email,
    fatherName: user.fatherName || '',
    rollNo: user.rollNo || '',
    batchId: user.batch?.id || '',
    password: '',
    confirmPassword: ''
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [stats, setStats] = useState({
    totalPractices: 0,
    averageAccuracy: 0,
    averageSpeed: 0,
    completedDocuments: 0
  });
  const [avatar, setAvatar] = useState(user.profilePicture || null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [batches, setBatches] = useState([]);

  useEffect(() => {
    const fetchStudentStats = async () => {
      try {
        const response = await apiService.getStudentStats();
        if (response.data.success) {
          setStats(response.data.stats);
        }
      } catch (err) {
        console.error('Error fetching student stats:', err);
      }
    };

    const fetchBatches = async () => {
      try {
        const response = await apiService.getActiveBatches();
        if (response.data.success) {
          setBatches(response.data.batches);
        }
      } catch (err) {
        console.error('Error fetching batches:', err);
      }
    };

    fetchStudentStats();
    fetchBatches();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatar(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (formData.password && formData.password !== formData.confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }

    try {
      const updateData = new FormData();
      updateData.append('username', formData.username);
      updateData.append('email', formData.email);
      updateData.append('fatherName', formData.fatherName);
      updateData.append('rollNo', formData.rollNo);
      updateData.append('batchId', formData.batchId);
      
      if (formData.password) {
        updateData.append('password', formData.password);
      }
      
      if (avatarFile) {
        updateData.append('profilePicture', avatarFile);
      }

      const response = await apiService.updateStudentProfile(updateData);

      if (response.data.success) {
        setMessage({ type: 'success', text: 'Profile updated successfully' });
        // Update local storage with new user data
        const updatedUser = { 
          ...user, 
          username: formData.username,
          email: formData.email,
          fatherName: formData.fatherName,
          rollNo: formData.rollNo,
          batch: batches.find(b => b.id === parseInt(formData.batchId)) || user.batch,
          profilePicture: response.data.profilePicture || avatar
        };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setIsEditing(false);
      } else {
        setMessage({ type: 'error', text: response.data.message || 'Update failed' });
      }
    } catch (err) {
      setMessage({ 
        type: 'error', 
        text: err.response?.data?.message || 'Error updating profile' 
      });
      console.error(err);
    }
  };

  // Get initials for avatar placeholder
  const getInitials = () => {
    return user.username
      .split(' ')
      .map(name => name[0])
      .join('')
      .toUpperCase();
  };

  return (
    <div className="student-profile-container">
      <div className="profile-card">
        <div className="profile-header">
          <div className="avatar-section">
            {avatar ? (
              <img src={avatar} alt={user.username} className="profile-avatar" />
            ) : (
              <div className="avatar-placeholder">
                {getInitials()}
              </div>
            )}
            {isEditing && (
              <div className="avatar-upload">
                <label htmlFor="avatar-input" className="avatar-edit-button">
                  <FaEdit />
                </label>
                <input
                  id="avatar-input"
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  className="hidden-input"
                />
              </div>
            )}
          </div>
          
          <div className="profile-title">
            <h2>{user.username}</h2>
            <span className="profile-role">
              <FaGraduationCap /> Student
            </span>
            <span className={`profile-status status-${user.status || 'active'}`}>
              {user.status === 'blocked' ? 'Blocked' : 'Active'}
            </span>
          </div>
          
          <button 
            className={`profile-edit-button ${isEditing ? 'cancel' : ''}`}
            onClick={() => setIsEditing(!isEditing)}
          >
            {isEditing ? (
              <>
                <FaTimes /> Cancel
              </>
            ) : (
              <>
                <FaEdit /> Edit Profile
              </>
            )}
          </button>
        </div>

        {message.text && (
          <div className={`message-alert ${message.type}`}>
            {message.type === 'success' ? <FaCheckCircle /> : null}
            {message.text}
          </div>
        )}

        <div className="profile-content">
          {isEditing ? (
            <form onSubmit={handleSubmit} className="profile-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Username</label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    required
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input"
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Father's Name</label>
                  <input
                    type="text"
                    name="fatherName"
                    value={formData.fatherName}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Roll Number</label>
                  <input
                    type="text"
                    name="rollNo"
                    value={formData.rollNo}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Batch</label>
                  <select
                    name="batchId"
                    value={formData.batchId}
                    onChange={handleChange}
                    className="form-input"
                  >
                    <option value="">Select Batch</option>
                    {batches.map(batch => (
                      <option key={batch.id} value={batch.id}>
                        {batch.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  {/* Empty group for alignment */}
                </div>
              </div>
              
              {/* <div className="form-row">
                <div className="form-group">
                  <label>New Password (leave blank to keep current)</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Confirm New Password</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>
              </div> */}
              
              <button type="submit" className="save-button">
                <FaSave /> Save Changes
              </button>
            </form>
          ) : (
            <div className="profile-details">
              <div className="details-section">
                <h3>Personal Information</h3>
                <div className="details-grid">
                  <div className="detail-item">
                    <span className="detail-label">Username</span>
                    <span className="detail-value">{user.username}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Email</span>
                    <span className="detail-value">{user.email}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Father's Name</span>
                    <span className="detail-value">{user.fatherName || 'Not provided'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Roll Number</span>
                    <span className="detail-value">{user.rollNo || 'Not assigned'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Batch</span>
                    <span className="detail-value">{user.enrolledBatches ? user.enrolledBatches?.[0]?.name : 'Not assigned'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">
                      <FaCalendarAlt /> Account Created
                    </span>
                    <span className="detail-value">{new Date(user.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* <div className="stats-card">
        <h3>Performance Statistics</h3>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-circle">
              <span className="stat-number">{stats.totalPractices}</span>
            </div>
            <span className="stat-title">Total Practices</span>
          </div>
          
          <div className="stat-card">
            <div className="stat-circle accuracy">
              <span className="stat-number">{stats.averageAccuracy}%</span>
            </div>
            <span className="stat-title">Average Accuracy</span>
          </div>
          
          <div className="stat-card">
            <div className="stat-circle speed">
              <span className="stat-number">{stats.averageSpeed}</span>
            </div>
            <span className="stat-title">Average Speed</span>
          </div>
          
          <div className="stat-card">
            <div className="stat-circle completed">
              <span className="stat-number">{stats.completedDocuments}</span>
            </div>
            <span className="stat-title">Completed Documents</span>
          </div>
        </div>
      </div> */}
    </div>
  );
}

export default StudentProfile;